import { useAuthStore } from './stores';

export interface FetcherError extends Error {
    info?: unknown;
    status?: number;
}

export async function AddressFetcher<T>(route: string) {
    const cloak = useAuthStore.getState().keyCloak;
    if (!cloak?.token) {
        throw new Error('Not authenticated');
    }
    await cloak.updateToken(5);

    const res = await fetch(`${process.env.EXPO_PUBLIC_ADDRESS_HOST ?? ''}/api/${route}`, {
        headers: new Headers({ Authorization: 'Bearer ' + cloak.token, 'Content-Type': 'application/json' }),
    });

    if (!res.ok) {
        const error: FetcherError = new Error('An error occurred while fetching the data.');
        // Attach extra info to the error object.
        error.info = await res.json();
        error.status = res.status;
        throw error;
    }

    return res.json() as T;
}

interface MutationOptions {
    arg: {
        body?: Record<string, unknown> | unknown[];
        method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
        replaceRoute?: string;
    };
}

export async function AddressMutate<T>(route: string, { arg }: MutationOptions) {
    const cloak = useAuthStore.getState().keyCloak;
    if (!cloak?.token) {
        throw new Error('Not authenticated');
    }
    await cloak.updateToken(5);

    const headers: Record<string, string> = {
        Authorization: 'Bearer ' + cloak.token,
    };

    if (arg.body) {
        headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(`${process.env.EXPO_PUBLIC_ADDRESS_HOST ?? ''}/api/${arg.replaceRoute ?? route}`, {
        body: arg.body ? JSON.stringify(arg.body) : undefined,
        headers: new Headers(headers),
        method: arg.method,
    });
    if (!res.ok) {
        const error: FetcherError = new Error('An error occurred while fetching the data.');
        // Attach extra info to the error object.
        error.info = await res.json();
        error.status = res.status;
        throw error;
    }

    if (res.status === 204) {
        return;
    }

    return res.json() as T;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function AddressFetchWithBody<T = any>(
    route: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    body: Record<string, unknown> | unknown[],
): Promise<T> {
    const cloak = useAuthStore.getState().keyCloak;
    if (!cloak?.token) {
        throw new Error('Not authenticated');
    }
    await cloak.updateToken(5);

    const res = await fetch(`${process.env.EXPO_PUBLIC_ADDRESS_HOST ?? ''}/api/${route}`, {
        body: JSON.stringify(body),
        headers: new Headers({
            Authorization: 'Bearer ' + cloak.token,
            'Content-Type': 'application/json',
        }),
        method: method,
    });

    if (!res.ok) {
        const error: FetcherError = new Error('An error occurred while fetching the data.');
        // Attach extra info to the error object.
        error.info = await res.json();
        error.status = res.status;
        throw error;
    }

    return res.json() as T;
}
